import { useState } from "react";
import classnames from "classnames";
import { Link } from "react-router-dom";
import {
  CollectionIcon,
  LightBulbIcon,
  UsersIcon,
  ChevronDownIcon,
} from "@heroicons/react/outline";
import { APP_ROUTES } from "const";
import { LayoutWrp } from "components";

interface INavItem {
  id: string;
  label: string;
  href: string;
  description: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}

const NAV_ITEMS: INavItem[] = [
  {
    id: "issues",
    label: "Issues",
    href: APP_ROUTES.ISSUES,
    description: "Explore community concerns ranked by impact and location.",
    Icon: CollectionIcon,
  },
  {
    id: "solutions",
    label: "Solutions",
    href: APP_ROUTES.SOLUTION_LIST,
    description: "Browse ideas being tested and refined by volunteers.",
    Icon: LightBulbIcon,
  },
  {
    id: "action-groups",
    label: "Action Groups",
    href: APP_ROUTES.ACTION_GROUP_LIST,
    description: "Find the teams and local groups behind each effort.",
    Icon: UsersIcon,
  },
];

function PillNav({ active, onSelect }: { active: string; onSelect: (id: string) => void }) {
  return (
    <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-white/80 px-3 py-1.5 shadow-lg shadow-slate-200/60 backdrop-blur">
      {NAV_ITEMS.map(({ id, label, Icon }) => {
        const isActive = id === active;
        return (
          <button
            key={id}
            type="button"
            onClick={() => onSelect(id)}
            className={classnames(
              "group inline-flex items-center gap-1 rounded-full px-4 py-2 text-sm font-medium transition-all",
              isActive
                ? "bg-gradient-to-r from-sky-500 to-indigo-500 text-white shadow"
                : "text-slate-600 hover:text-slate-900"
            )}
          >
            <Icon className={classnames("h-4 w-4", isActive ? "text-white" : "text-slate-400 group-hover:text-slate-500")}
            />
            <span>{label}</span>
          </button>
        );
      })}
    </div>
  );
}

function MobileTabRail({ active, onSelect }: { active: string; onSelect: (id: string) => void }) {
  return (
    <div className="flex w-full snap-x snap-mandatory overflow-x-auto rounded-full border border-slate-200 bg-white/80 p-1 shadow-sm shadow-slate-200/50">
      {NAV_ITEMS.map(({ id, label, Icon }) => {
        const isActive = id === active;
        return (
          <button
            key={id}
            onClick={() => onSelect(id)}
            type="button"
            className={classnames(
              "snap-center shrink-0 grow basis-[45%] rounded-full px-3 py-2 text-xs font-semibold transition",
              isActive
                ? "bg-gradient-to-br from-sky-500 to-indigo-500 text-white shadow"
                : "text-slate-600 hover:text-slate-900"
            )}
          >
            <div className="flex items-center justify-center gap-2">
              <Icon className={classnames("h-4 w-4", isActive ? "text-white" : "text-slate-400")}
              />
              <span>{label}</span>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function BottomNav({ active }: { active: string }) {
  return (
    <nav className="flex w-full items-center justify-around rounded-2xl border border-slate-200 bg-white/95 px-4 py-3 shadow-xl shadow-slate-200/70">
      {NAV_ITEMS.map(({ id, label, Icon }) => {
        const isActive = id === active;
        return (
          <Link
            key={id}
            to={"#"}
            className={classnames(
              "flex flex-col items-center gap-1 text-xs font-semibold transition",
              isActive ? "text-sky-600" : "text-slate-500 hover:text-slate-800"
            )}
          >
            <span
              className={classnames(
                "flex h-10 w-10 items-center justify-center rounded-full border",
                isActive
                  ? "border-sky-200 bg-sky-50 text-sky-600"
                  : "border-transparent bg-slate-50"
              )}
            >
              <Icon className="h-5 w-5" />
            </span>
            {label}
          </Link>
        );
      })}
    </nav>
  );
}

function MobileDockFullWidth({ active }: { active: string }) {
  return (
    <nav className="flex w-full items-center justify-around bg-slate-900 text-slate-100 py-3">
      {NAV_ITEMS.map(({ id, label, Icon }) => {
        const isActive = id === active;
        return (
          <Link
            key={id}
            to={"#"}
            className={classnames(
              "flex flex-col items-center gap-1 text-xs font-semibold transition",
              isActive ? "text-white" : "text-slate-300 hover:text-white"
            )}
          >
            <span
              className={classnames(
                "flex h-10 w-10 items-center justify-center rounded-full border",
                isActive ? "border-white/40 bg-indigo-500" : "border-transparent"
              )}
            >
              <Icon className="h-5 w-5" />
            </span>
            {label}
          </Link>
        );
      })}
    </nav>
  );
}

function DesktopBottomDock({ active }: { active: string }) {
  return (
    <div className="relative w-full overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-lg shadow-slate-300/70">
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-sky-400 via-indigo-500 to-purple-500" />
      <nav className="grid grid-cols-3 divide-x divide-slate-200">
        {NAV_ITEMS.map(({ id, label, Icon }) => {
          const isActive = id === active;
          return (
            <Link
              key={id}
              to={"#"}
              className={classnames(
                "flex flex-col items-center gap-2 px-6 py-5 text-sm font-semibold transition",
                isActive ? "bg-slate-50 text-indigo-600" : "text-slate-600 hover:bg-slate-50"
              )}
            >
              <span
                className={classnames(
                  "flex h-11 w-11 items-center justify-center rounded-full border",
                  isActive ? "border-indigo-200 bg-indigo-100" : "border-slate-200"
                )}
              >
                <Icon className={classnames("h-5 w-5", isActive ? "text-indigo-600" : "text-slate-400")}
                />
              </span>
              {label}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}

function HeroReflowPreview({ active }: { active: string }) {
  return (
    <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-lg shadow-slate-200/70">
      <div className="border-b border-slate-200 bg-gradient-to-br from-white via-white to-slate-100 px-8 py-6">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3">
            <span className="rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold text-indigo-600">
              Hero concept
            </span>
            <span className="text-xs uppercase tracking-wide text-slate-500">
              Desktop layout
            </span>
          </div>
          <div className="hidden items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 shadow-sm shadow-slate-200/60 lg:flex">
            {NAV_ITEMS.map(({ id, label, Icon }) => {
              const isActive = id === active;
              return (
                <span
                  key={id}
                  className={classnames(
                    "flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold",
                    isActive
                      ? "bg-gradient-to-r from-sky-500 to-indigo-500 text-white"
                      : "text-slate-600"
                  )}
                >
                  <Icon className={classnames("h-4 w-4", isActive ? "text-white" : "text-slate-400")}
                  />
                  {label}
                </span>
              );
            })}
          </div>
        </div>

        <div className="mt-6 grid gap-8 lg:grid-cols-[1.15fr_0.85fr] lg:items-center">
          <div className="space-y-5">
            <h3 className="text-2xl font-bold text-slate-900">
              Fewer distractions, clearer next steps
            </h3>
            <p className="text-sm text-slate-600">
              Fold the search, filters, and launch actions into two tight rows so
              the hero headline can breathe. After 240px of scroll, the hero
              compacts and the bottom dock fades in to keep context.
            </p>
            <div className="flex flex-wrap gap-3">
              <span className="rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-700">
                Report Issue CTA
              </span>
              <span className="rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-700">
                Quick Metrics
              </span>
              <span className="rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-700">
                Learn More
              </span>
            </div>
          </div>

          <div className="relative">
            <div className="aspect-[4/3] w-full overflow-hidden rounded-2xl bg-gradient-to-br from-sky-500 to-indigo-600 text-white shadow-xl shadow-indigo-500/40">
              <div className="absolute inset-4 rounded-xl border border-white/30 bg-white/10 backdrop-blur">
                <p className="px-6 py-6 text-sm font-semibold">
                  Scroll transition: hero compresses to 120px, while the dock
                  anchors key destinations at the bottom of the viewport.
                </p>
              </div>
              <div className="absolute bottom-5 left-1/2 hidden w-[85%] -translate-x-1/2 rounded-2xl border border-white/40 bg-white/90 px-5 py-3 text-xs font-semibold text-slate-700 shadow-lg shadow-slate-900/30 lg:flex lg:items-center lg:justify-between">
                <span>Dock Preview</span>
                <span className="flex items-center gap-2 text-indigo-600">
                  Issues
                  <span className="h-1.5 w-1.5 rounded-full bg-indigo-500" />
                  Solutions
                  <span className="h-1.5 w-1.5 rounded-full bg-sky-400" />
                  Action Groups
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-3 bg-slate-50 px-8 py-6 text-sm text-slate-600">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-indigo-500" />
          <span>Hero height reduces after the first viewport to keep filters visible.</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-sky-500" />
          <span>Dock activates with subtle motion once the hero condenses.</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-violet-500" />
          <span>Search inputs rest under the headline for faster eye scanning.</span>
        </div>
      </div>
    </div>
  );
}

function SidebarPreview({ active }: { active: string }) {
  return (
    <aside className="w-full max-w-xs rounded-2xl border border-slate-200 bg-white shadow-md">
      <div className="border-b border-slate-200 px-5 py-4">
        <p className="text-sm font-semibold text-slate-800">Primary pages</p>
        <p className="text-xs text-slate-500">Visible by default, quick jump between core sections.</p>
      </div>
      <ul className="space-y-1 px-3 py-4">
        {NAV_ITEMS.map(({ id, label }) => {
          const isActive = id === active;
          return (
            <li key={id}>
              <Link
                to={"#"}
                className={classnames(
                  "flex items-center justify-between rounded-xl px-3 py-2 text-sm font-medium transition",
                  isActive
                    ? "bg-sky-50 text-sky-700"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                )}
              >
                {label}
              </Link>
            </li>
          );
        })}
      </ul>
      <div className="border-t border-slate-200">
        <button className="flex w-full items-center justify-between px-5 py-4 text-left text-sm font-semibold text-slate-700 transition hover:bg-slate-50">
          More
          <ChevronDownIcon className="h-4 w-4 text-slate-400" />
        </button>
        <div className="space-y-1 bg-slate-50 px-3 py-3 text-sm text-slate-500">
          <p>Collapsed by default, expands on click or if a nested page is active.</p>
          <p className="text-xs">Keeps focus on the three discovery pages for new users.</p>
        </div>
      </div>
    </aside>
  );
}

export function NavigationShowcase() {
  const [active, setActive] = useState<string>(NAV_ITEMS[0].id);

  const activeCopy = NAV_ITEMS.find((item) => item.id === active);

  return (
    <LayoutWrp className="bg-slate-100 px-4 py-10 sm:px-8">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-12">
        <header className="space-y-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-sky-600">Mock-up</p>
          <h1 className="text-3xl font-bold text-slate-900">
            Navigation concepts for Issues, Solutions & Action Groups
          </h1>
          <p className="max-w-2xl text-base text-slate-600">
            A responsive tab experience that keeps the three discovery pages one tap away. The active state is shown in indigo, while inactive pills stay subtle so the content takes center stage.
          </p>
        </header>

        <section className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="space-y-10">
            <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-8 shadow-lg shadow-slate-200/70">
              <h2 className="text-lg font-semibold text-slate-900">Desktop floating pill navigation</h2>
              <p className="mt-2 text-sm text-slate-500">
                Lives under the sticky header. Pills glide into an active gradient state and shrink to icon-only on narrow widths.
              </p>
              <div className="mt-6 flex justify-center">
                <PillNav active={active} onSelect={setActive} />
              </div>
              {activeCopy && (
                <div className="mt-6 rounded-2xl border border-slate-200 bg-white/70 p-5 text-sm text-slate-600">
                  <p className="font-semibold text-slate-800">{activeCopy.label}</p>
                  <p className="mt-1">{activeCopy.description}</p>
                </div>
              )}
            </div>

          <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-8 shadow-lg shadow-slate-200/70">
            <h2 className="text-lg font-semibold text-slate-900">Mobile tab rail & one-hand bottom nav</h2>
            <p className="mt-2 text-sm text-slate-500">
              Top rail scrolls horizontally with snap, while the bottom bar offers thumb-friendly access.
            </p>
              <div className="mt-6 grid gap-6 md:grid-cols-2">
                <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow">
                  <p className="text-xs font-semibold uppercase text-slate-500">Top rail</p>
                  <div className="mt-4">
                    <MobileTabRail active={active} onSelect={setActive} />
                  </div>
                </div>
                <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow">
                  <p className="text-xs font-semibold uppercase text-slate-500">Bottom nav</p>
                  <div className="mt-4">
                    <BottomNav active={active} />
                  </div>
                </div>
                <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow">
                  <p className="text-xs font-semibold uppercase text-slate-500">Bottom dock (full width)</p>
                  <div className="mt-4 overflow-hidden rounded-2xl">
                    <MobileDockFullWidth active={active} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-8">
            <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-8 shadow-lg shadow-slate-200/70">
              <h2 className="text-lg font-semibold text-slate-900">Sidebar concept</h2>
              <p className="mt-2 text-sm text-slate-500">
                Primary destinations sit outside the accordion so new visitors see them instantly. The “More” group stays collapsed until tapped or when a nested link is active.
              </p>
              <div className="mt-6 flex justify-center">
                <SidebarPreview active={active} />
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-8 shadow-lg shadow-slate-200/70">
              <h2 className="text-lg font-semibold text-slate-900">Desktop footer dock experiment</h2>
              <p className="mt-2 text-sm text-slate-500">
                An optional bottom treatment that keeps primary navigation visible after users scroll through dense filters.
              </p>
              <div className="mt-6">
                <DesktopBottomDock active={active} />
              </div>
              <ul className="mt-6 list-disc space-y-2 pl-5 text-sm text-slate-600">
                <li>Dock appears after the hero to avoid crowding the header.</li>
                <li>Gradient accent bar hints at motion and active state.</li>
                <li>Consider hiding when forms or modals overlap the footer.</li>
              </ul>
            </div>

            <HeroReflowPreview active={active} />

            <div className="rounded-3xl border border-dashed border-slate-300 bg-white/50 p-6 text-sm text-slate-600">
              <p className="font-semibold text-slate-800">How to explore</p>
              <ol className="mt-3 list-decimal space-y-2 pl-5">
                <li>Use the pills above to toggle the active page state.</li>
                <li>Notice how copy, icons, and gradients respond across desktop and mobile treatments.</li>
                <li>Consider keeping the bottom bar for signed-out visitors to boost early exploration.</li>
              </ol>
            </div>
          </div>
        </section>
      </div>
    </LayoutWrp>
  );
}

export default NavigationShowcase;
